import React, { useState } from 'react';
import { Search, MapPin, GraduationCap, ArrowUpRight, SearchX } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { universities, countries } from '../mock';
import Reveal from './shared/Reveal';
import SectionHeader from './shared/SectionHeader';

const UniversityFinder = () => {
  const [selectedCountry, setSelectedCountry] = useState('all');
  const [selectedDegree, setSelectedDegree] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredUniversities = universities.filter((uni) => {
    const matchesCountry = selectedCountry === 'all' || uni.country === selectedCountry;
    const matchesDegree = selectedDegree === 'all' || uni.degree === selectedDegree;
    const matchesSearch = uni.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCountry && matchesDegree && matchesSearch;
  });

  return (
    <section id="universities" className="py-16 lg:py-20 bg-white">
      <div className="container-x">
        <SectionHeader
          eyebrow="University finder"
          title={<>Find your perfect <span className="text-brand-500">university</span></>}
          subtitle="Search our database of top universities worldwide - filter by country and degree level."
        />

        <Reveal>
          <div className="rounded-3xl bg-navy-50/70 border border-navy-100 p-6 sm:p-8 mb-10">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="md:col-span-2">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                  <Input
                    type="text"
                    placeholder="Search universities..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-11 h-12 bg-white rounded-xl border-navy-100"
                  />
                </div>
              </div>
              <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                <SelectTrigger className="h-12 bg-white rounded-xl border-navy-100">
                  <SelectValue placeholder="All Countries" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Countries</SelectItem>
                  {countries.map((country) => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={selectedDegree} onValueChange={setSelectedDegree}>
                <SelectTrigger className="h-12 bg-white rounded-xl border-navy-100">
                  <SelectValue placeholder="All Degrees" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Degrees</SelectItem>
                  <SelectItem value="Bachelors">Bachelors</SelectItem>
                  <SelectItem value="Masters">Masters</SelectItem>
                  <SelectItem value="PhD">PhD</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Reveal>

        <p className="text-sm font-semibold text-slate-400 mb-5">
          Showing {filteredUniversities.length} universit{filteredUniversities.length === 1 ? 'y' : 'ies'}
        </p>

        {filteredUniversities.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filteredUniversities.map((uni, index) => {
              const askUrl =
                'https://wa.me/919059333987?text=' +
                encodeURIComponent('Hi, I am interested in ' + uni.name + ' (' + uni.country + '). Can you guide me?');
              return (
                <Reveal key={uni.id} delay={(index % 3) * 90}>
                  <div className="card-premium group p-6 h-full flex flex-col">
                    <div className="flex items-start justify-between gap-3">
                      <h3 className="font-display text-lg font-bold text-navy-900 group-hover:text-brand-600 transition-colors">
                        {uni.name}
                      </h3>
                      <span className="shrink-0 rounded-full bg-navy-900 text-white text-xs font-bold px-2.5 py-1">
                        #{uni.ranking}
                      </span>
                    </div>
                    <div className="mt-3 space-y-1.5 flex-1">
                      <p className="flex items-center gap-2 text-sm text-slate-500">
                        <MapPin className="w-4 h-4 text-brand-500" />
                        {uni.country}
                      </p>
                      <p className="flex items-center gap-2 text-sm text-slate-500">
                        <GraduationCap className="w-4 h-4 text-brand-500" />
                        {uni.degree}
                      </p>
                    </div>
                    <a
                      href={askUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-navy-900 group-hover:text-brand-600 transition-colors"
                    >
                      Ask about this university
                      <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </a>
                  </div>
                </Reveal>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-20 rounded-3xl border border-dashed border-navy-200">
            <SearchX className="w-10 h-10 text-navy-200 mx-auto mb-4" />
            <h3 className="font-display text-xl font-bold text-navy-900 mb-1.5">No universities found</h3>
            <p className="text-slate-500 text-sm">Try adjusting your search criteria</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default UniversityFinder;
